import { extend } from 'lodash'
import { createSlice } from '@reduxjs/toolkit'

const initialState = {
	parent: null,
	modelId: null,
	documentTitle: '',

	data: [],
	visible: [],
	conditionals: [],

	currentPage: 0,
	lastPage: 0,

	focusedField: null,

	error: null,
	loading: false,
	preview: false,
	uploadedFile: null,
	contextAi: null,
}

const { actions, reducer } = createSlice({
	name: 'question',
	initialState,
	reducers: {
		listQuestion: (state) =>
			extend(state, {
				loading: true,
			}),
		listQuestionSuccess: (state, { payload }) => {
			const { modelId, documentTitle, parent, data } = payload
			extend(state, {
				parent,
				modelId,
				documentTitle,
				templateName: data.name,
				textType: data.text_type,
				data: data.form,
				error: null,
				loading: true,
				currentPage: 0,
				focusedField: null,
				preview: data.preview,
				lastPage: data.form.length - 1,
				visible: data.form.map((page) => {
					return page.fields.map((field) => {
						return !field.condition
					})
				}),
				conditionals: (() => {
					let variables = new Set()
					for (let page of data.form) {
						for (let field of page.fields) {
							if ('condition' in field) {
								if (Array.isArray(field.condition))
									field.condition.forEach((cond) =>
										variables.add(cond.variable)
									)
								else variables.add(field.condition.variable)
							}
						}
					}

					return [...variables]
				})(),
			})
		},
		listQuestionFailure: (state, { payload }) =>
			extend(state, {
				error: payload.error,
				loading: false,
			}),
		setLoadingAI: (state, { payload }) => {
			state.loading = payload
		},
		updateFieldsWithAIResponse: (state, { payload }) => {
			const { responses } = payload

			state.data = state.data.map((page) => ({
				...page,
				fields: page.fields.map((field) => ({
					...field,
					initialValue: responses[field.prompt] || field.initialValue,
				})),
			}))

			state.loading = false
		},
		updateVisible: (state, { payload }) => {
			const compareCondition = (condition, input) => {
				const { operator, value } = condition

				// Type coercion is used here to compare strings and numbers
				/* eslint-disable eqeqeq*/
				let comparison
				switch (operator) {
					case '>':
						comparison = input > value
						break
					case '>=':
						comparison = input >= value
						break
					case '<':
						comparison = input < value
						break
					case '<=':
						comparison = input <= value
						break
					case '=':
						if (Array.isArray(input)) {
							comparison =
								input.length === value.length &&
								value.every((v) => input.includes(v))
						} else {
							comparison = compare(input, value, (a, b) => a == b)
						}
						break
					case '!=':
						comparison = compare(input, value, (a, b) => a != b)
						break

					case 'contains':
						if (Array.isArray(input)) {
							const inputValue =
								typeof input[0] === 'object' ? input[0]?.id : input[0]
							input = Number.isInteger(inputValue)
								? input.map((v) => {
										if (typeof v === 'object') {
											return v?.id?.toString()
										}

										return v.toString()
									})
								: input

							comparison = value.every((v) => input.includes(v))
						}

						break

					// 'contains' requires every value in `condition.value` to be present
					// in `input` (subset check). 'intersects' only requires at least one
					// match (OR semantics) - needed for multi-select fields (e.g. a
					// checkbox-based JURISDICTION) where a condition should fire if ANY
					// of several accepted values was selected, not all of them.
					case 'intersects':
						if (Array.isArray(input)) {
							comparison = value.some((v) => input.includes(v))
						} else {
							comparison = value.includes(input)
						}

						break
					default:
						comparison = false
						break
				}
				/* eslint-enable eqeqeq*/

				return comparison
			}

			let { form, input, subfield /* fieldIndex, pageIndex */ } = payload
			let variableName

			if (typeof input !== 'undefined') {
				// let pageFieldsData = state.data[pageIndex].fields

				// This 'if' is here so templates whose variables are not objects still work
				// if (typeof pageFieldsData[fieldIndex].variable === 'string') {
				// 	variableName = pageFieldsData[fieldIndex].variable
				// } else {
				// 	variableName = pageFieldsData[fieldIndex].variable.name
				// }

				variableName = subfield
					? [variableName, subfield].join('.')
					: variableName
			}

			let visible = [...state.visible].map((pageFieldsVisible) => [
				...pageFieldsVisible,
			])

			state.data.forEach((page, pageIndex) =>
				page.fields.forEach((field, fieldIndex) => {
					if (field.condition) {
						let conditions = Array.isArray(field.condition)
							? field.condition
							: [field.condition]

						let comparison = conditions.map((condition) => {
							let value
							if (condition.variable === variableName) {
								value = input
							} else {
								let variables = condition.variable.split('.')

								value = form.getFieldValue(variables[0])
								if (variables.length === 2 && value !== undefined)
									value = value[variables[1]]
							}
							return compareCondition(condition, value)
						})
						visible[pageIndex][fieldIndex] = comparison.every((i) => i === true)
					}
				})
			)

			let lastPage = state.data.length - 1
			while (
				lastPage > 0 &&
				visible[lastPage].every((fieldVisible) => fieldVisible === false)
			) {
				lastPage--
			}

			return { ...state, visible, lastPage }
		},
		nextPage: (state, { payload: { hasPreview = false } }) => {
			let next = state.currentPage + 1
			const lastPage = hasPreview ? state.lastPage + 1 : state.lastPage

			if (next > lastPage) return

			// Skip empty form pages
			if (Array.isArray(state.visible)) {
				while (
					next < lastPage &&
					(state.visible[next].every(
						(fieldVisible) => fieldVisible === false
					) ||
						next === lastPage)
				) {
					next++
				}
			}

			extend(state, { currentPage: next })
		},
		previousPage: (state) => {
			let next = state.currentPage - 1
			if (next < 0) return

			// Skip empty form pages
			if (Array.isArray(state.visible)) {
				while (
					next > 0 &&
					state.visible[next].every((fieldVisible) => fieldVisible === false)
				) {
					next--
				}
			}

			extend(state, { currentPage: next })
		},
		focusField: (state, { payload }) => {
			extend(state, { focusedField: payload.name })
		},
		setResetQuestion: (state) => {
			extend(state, {
				...initialState,
			})
		},
		uploadExternalFile: (state, { payload }) =>
			extend(state, {
				uploadedFile: payload.uploadedFile,
			}),
		uploadContextFile: (state, { payload }) =>
			extend(state, {
				contextAi: payload.context,
			}),
		removeContextFile: (state) =>
			extend(state, {
				contextAi: null,
			}),
	},
})

function compare(input, value, comparisonFn) {
	// If value is an array, OR logic is applied
	if (Array.isArray(value)) {
		for (let item of value) {
			if (comparisonFn(input, item)) return true
		}
	} else {
		return comparisonFn(input, value)
	}
}

export const {
	listQuestion,
	listQuestionSuccess,
	listQuestionFailure,
	setLoadingAI,
	updateFieldsWithAIResponse,
	updateVisible,
	nextPage,
	previousPage,
	focusField,
	setResetQuestion,
	uploadExternalFile,
	uploadContextFile,
	removeContextFile,
} = actions

export { default as questionSaga } from './sagas'

export default reducer
